/**
 * Incorporation Variable Mapper
 *
 * INCORPORATION_DATA의 Directors/Shareholders/회사 정보를
 * 각 템플릿 변수에 자동 매핑
 *
 * CDD 템플릿 변수 접두사:
 *   ind_ = Individual, pt_ = Personal/Contact, cdd_ = CDD 공통
 * Section201 변수:
 *   name, nationality, nric, passport, declaration_date 등
 * Section14 변수:
 *   company 관련 + 모든 director/shareholder 반복부
 */

import type { IncorporationQAState } from '../types/incorporation.types';
import { formatDocDate } from '@/shared/composables/dateUtils';

// ── Helpers ───────────────────────────────────────────────────

function isCodeValue(val: string): boolean {
  if (!val) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)) return true;
  if (/^[A-Z]\d{2,5}$/i.test(val)) return true;
  return false;
}

// 주소 필드에서 사람이 읽을 수 있는 값을 얻는다.
//  1) 이미 resolve된 `*Name` 필드가 있으면 그 값 (예: stateName="Perlis")
//  2) 그렇지 않고 원본 값이 UUID/code가 아니면 원본 사용
//  3) 그 외에는 빈 문자열 (UUID 노출 방지)
function resolveAddrField(addr: Record<string, any> | undefined, key: 'city' | 'state' | 'postcode' | 'country'): string {
  if (!addr) return '';
  const nameField = `${key}Name`;
  const resolved = addr[nameField];
  if (resolved && String(resolved).trim() !== '' && !isCodeValue(String(resolved))) {
    return String(resolved).trim();
  }
  const rawCandidates: any[] = key === 'postcode'
    ? [addr.postcode, addr.post_code, addr.postalCode]
    : key === 'city'
      ? [addr.city, addr.town]
      : [addr[key]];
  for (const raw of rawCandidates) {
    if (!raw) continue;
    const s = String(raw).trim();
    if (s && !isCodeValue(s)) return s;
  }
  return '';
}

/** UUID/코드가 아닌 표시값만 허용 — *Name 우선. */
function safeAddrPart(raw: any, name: any): string {
  if (name != null && String(name).trim() !== '') return String(name).trim();
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  return isCodeValue(s) ? '' : s;
}

/** 주소 표시 — 각 단어 첫 글자만 대문자 (Contact Detail / AOD 와 동일). */
function toTitleCaseAddress(str: string): string {
  if (!str) return '';
  return str.replace(/[A-Za-z]+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function formatAddrDisplayPart(part: string, kind?: 'postcode'): string {
  const s = String(part || '').trim();
  if (!s) return '';
  if (kind === 'postcode' || /^\d{4,6}$/.test(s)) return s;
  return toTitleCaseAddress(s);
}

/** Malaysia NRIC 표준 포맷: 12자리 숫자면 XXXXXX-XX-XXXX. 여권 등은 원문 유지. */
function formatMalaysiaNricForDoc(raw: any): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const digits = s.replace(/\D/g, '');
  if (digits.length === 12) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  return s;
}

function isPassportIdType(idType: any): boolean {
  return /passport/i.test(String(idType ?? '').trim());
}

/** NRIC/IC 번호는 하이픈 포맷, 여권·기타는 원문. */
function formatIdNumberForDoc(idNumber: any, idType?: any): string {
  const s = String(idNumber ?? '').trim();
  if (!s) return '';
  if (isPassportIdType(idType)) return s;
  return formatMalaysiaNricForDoc(s);
}

/**
 * MY incorporation 주소 한 줄 — street, postcode, city, state, country (쉼표 구분, Title Case).
 */
function formatIncorporationAddressLine(addr: Record<string, any> | undefined): string {
  if (!addr || typeof addr !== 'object') return '';
  const segments: Array<{ v: string; kind?: 'postcode' }> = [
    { v: String(addr.street01 || addr.addressLine1 || '').trim() },
    { v: String(addr.street02 || addr.addressLine2 || '').trim() },
    { v: String(addr.street03 || addr.addressLine3 || '').trim() },
    { v: safeAddrPart(addr.postcode ?? addr.post_code, addr.postcodeName), kind: 'postcode' },
    { v: safeAddrPart(addr.city, addr.cityName) },
    { v: safeAddrPart(addr.state, addr.stateName) },
    { v: safeAddrPart(addr.country, addr.countryName) },
  ];
  return segments
    .map(({ v, kind }) => formatAddrDisplayPart(v, kind))
    .filter(Boolean)
    .join(', ');
}

function formatPrecomposedAddressLine(line: string): string {
  const s = String(line || '').trim();
  if (!s) return '';
  return s.split(',').map((p) => formatAddrDisplayPart(p.trim())).filter(Boolean).join(', ');
}

/** 후보 중 쉼표 파트가 더 많은(= postcode/city/state 포함) 주소 선택. */
function pickBestAddressLine(...candidates: Array<any>): string {
  const list = candidates
    .map((c) => (c == null ? '' : String(c).trim()))
    .filter(Boolean);
  if (list.length === 0) return '';
  const score = (s: string): number => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    return parts.length * 10 + Math.min(s.length, 200) / 50;
  };
  return list
    .map((s) => ({ s, sc: score(s) }))
    .sort((a, b) => b.sc - a.sc)[0].s;
}

function resolveCompanyRegisteredAddress(companyAddr: Record<string, any>): string {
  const precomposed = formatPrecomposedAddressLine(String(companyAddr.registered_address || '').trim());
  const built = formatIncorporationAddressLine({
    street01: companyAddr.addressLine1,
    street02: companyAddr.addressLine2,
    street03: companyAddr.addressLine3,
    postcode: companyAddr.postcode,
    city: companyAddr.city,
    state: companyAddr.state,
    country: companyAddr.country,
    postcodeName: companyAddr.postcodeName,
    cityName: companyAddr.cityName,
    stateName: companyAddr.stateName,
    countryName: companyAddr.countryName,
  });
  return pickBestAddressLine(precomposed, built);
}

function resolveCompanyBusinessAddress(companyAddr: Record<string, any>): string {
  const precomposed = formatPrecomposedAddressLine(String(companyAddr.business_address || '').trim());
  const built = formatIncorporationAddressLine({
    street01: companyAddr.businessAddressLine1,
    street02: companyAddr.businessAddressLine2,
    street03: companyAddr.businessAddressLine3,
    postcode: companyAddr.businessPostcode,
    city: companyAddr.businessCity,
    state: companyAddr.businessState,
    country: companyAddr.businessCountry,
    postcodeName: companyAddr.businessPostcodeName,
    cityName: companyAddr.businessCityName,
    stateName: companyAddr.businessStateName,
    countryName: companyAddr.businessCountryName,
  });
  return pickBestAddressLine(precomposed, built);
}

function directorAddr(raw?: Record<string, any>): Record<string, any> {
  return raw?.myDirectorDetails?.address || raw?.myDetails?.address || {};
}

function shareholderAddr(raw?: Record<string, any>): Record<string, any> {
  return raw?.myShareholderDetails?.address || raw?.myDetails?.address || {};
}

function readFlatField(raw: Record<string, any>, keys: string[]): string {
  for (const k of keys) {
    const v = String(raw[k] || '').trim();
    if (v && !isCodeValue(v)) return v;
  }
  return '';
}

function formatFlatAddrField(value: string, key: 'postcode' | 'city' | 'state'): string {
  if (!value) return '';
  return formatAddrDisplayPart(value, key === 'postcode' ? 'postcode' : undefined);
}

/** 저장 시 resolve 된 flat 필드(director_postcode 등) + 주소 객체 필드만 사용. */
function resolveDirectorFlatField(
  raw: Record<string, any> | undefined,
  key: 'postcode' | 'city' | 'state',
): string {
  if (!raw) return '';
  const flatKeys: Record<string, string[]> = {
    postcode: ['director_postcode', 'directorPostcode'],
    city: ['director_town', 'directorTown'],
    state: ['director_state', 'directorState'],
  };
  const fromFlat = readFlatField(raw, flatKeys[key]);
  if (fromFlat) return formatFlatAddrField(fromFlat, key);
  const fromAddr = resolveAddrField(directorAddr(raw), key);
  if (fromAddr) return formatFlatAddrField(fromAddr, key);
  return '';
}

function resolveShareholderFlatField(
  raw: Record<string, any> | undefined,
  key: 'postcode' | 'city' | 'state',
): string {
  if (!raw) return '';
  const flatKeys: Record<string, string[]> = {
    postcode: ['member_postcode', 'memberPostcode'],
    city: ['member_town', 'memberTown'],
    state: ['member_state', 'memberState'],
  };
  const fromFlat = readFlatField(raw, flatKeys[key]);
  if (fromFlat) return formatFlatAddrField(fromFlat, key);
  const fromAddr = resolveAddrField(shareholderAddr(raw), key);
  if (fromAddr) return formatFlatAddrField(fromAddr, key);
  return '';
}

function resolveDirectorFullAddress(raw?: Record<string, any>): string {
  return pickBestAddressLine(
    formatPrecomposedAddressLine(String(raw?.director_address || '')),
    formatIncorporationAddressLine(directorAddr(raw)),
  );
}

function resolveShareholderFullAddress(raw?: Record<string, any>): string {
  return pickBestAddressLine(
    formatPrecomposedAddressLine(String(raw?.member_address || '')),
    formatIncorporationAddressLine(shareholderAddr(raw)),
  );
}

function formatAddress(addr: Record<string, any> | undefined): string {
  return formatIncorporationAddressLine(addr);
}

function formatStreetOnly(addr: Record<string, any> | undefined): string {
  if (!addr) return '';
  return [
    addr.street01 || addr.addressLine1,
    addr.street02 || addr.addressLine2,
    addr.street03 || addr.addressLine3,
  ]
    .map((p) => formatAddrDisplayPart(String(p ?? '').trim()))
    .filter(Boolean)
    .join(', ');
}

function normalize(key: string): string {
  return key.toLowerCase().replace(/[\s_-]+/g, '');
}

function matches(varKey: string, ...patterns: string[]): boolean {
  const n = normalize(varKey);
  return patterns.some(p => n.includes(normalize(p)));
}

// ── Company Fields ────────────────────────────────────────────

export function mapCompanyFields(
  varKey: string,
  incorporationData: Record<string, any>,
  qa: IncorporationQAState,
): string | null {
  const n = normalize(varKey);

  // 회사명
  if (matches(varKey, 'companyname', 'company_name', 'entityname', 'ind_full_name')) {
    const name = incorporationData.companyName || qa.companyName || '';
    const suffix = incorporationData.companySuffix || '';
    // ind_full_name은 회사명이 아닌 개인 이름이어야 → 아래 director에서 처리
    if (matches(varKey, 'ind_full_name')) return null;
    return suffix ? `${name} ${suffix}` : name;
  }
  if (matches(varKey, 'companysuffix', 'suffix')) {
    return incorporationData.companySuffix || '';
  }
  if (matches(varKey, 'companyfullname', 'fullname', 'full_company')) {
    const name = incorporationData.companyName || qa.companyName || '';
    const suffix = incorporationData.companySuffix || '';
    return suffix ? `${name} ${suffix}` : name;
  }
  if (matches(varKey, 'registrationno', 'registration_no', 'regno', 'companyno')) {
    return incorporationData.registrationNo || '';
  }
  if (matches(varKey, 'placeofincorporation', 'place_of_incorporation')) {
    return 'Malaysia';
  }
  // 회사 주소 — MY 저장 시 registered_address/business_address(조합 문자열) 우선
  const companyAddr = (incorporationData.address || {}) as Record<string, any>;
  if (matches(varKey, 'registeredofficeaddress', 'registered_office_address')) {
    return resolveCompanyRegisteredAddress(companyAddr);
  }
  if (matches(varKey, 'businessaddress', 'business_address') && !matches(varKey, 'registered')) {
    return resolveCompanyBusinessAddress(companyAddr);
  }
  // 사업 활동
  if (matches(varKey, 'primaryactivity', 'businessactivity', 'activity1')) {
    return incorporationData.primaryActivity || '';
  }
  if (matches(varKey, 'secondaryactivity', 'activity2')) {
    return incorporationData.secondaryActivity || '';
  }
  // 통화 / 자본금
  if (matches(varKey, 'currency', 'currencycd')) {
    return incorporationData.currencyCd || '';
  }
  if (matches(varKey, 'minimumcapital', 'capital', 'sharecapital')) {
    return String(incorporationData.minimumCapital || '');
  }
  // CDD 날짜 — 세션 dateFormat 적용 (jurisdiction 시간대 + user preference)
  if (matches(varKey, 'cdd_date', 'cdddate')) {
    return formatDocDate(new Date()) || new Date().toISOString().split('T')[0];
  }
  // Declaration date — 세션 dateFormat 적용
  if (matches(varKey, 'declaration_date', 'declarationdate')) {
    return formatDocDate(new Date()) || new Date().toISOString().split('T')[0];
  }

  // ── 회사 레벨 확장 필드 (Section 14 SSM Super Form 등) ──────────
  // 회사 구분 / 목적
  if (matches(varKey, 'lodging_reference_no', 'lodgingreferenceno', 'lodging_ref')) {
    return incorporationData.lodging_reference_no || '';
  }
  if (matches(varKey, 'purpose')) return incorporationData.purpose || '';
  if (matches(varKey, 'company_type', 'companytype')) return incorporationData.company_type || '';
  if (matches(varKey, 'sub_type', 'subtype')) return incorporationData.sub_type || '';
  if (matches(varKey, 'register_type', 'registertype')) return incorporationData.registerType || '';
  if (matches(varKey, 'registration_number', 'registrationnumber')) {
    return incorporationData.registrationNumber || incorporationData.registrationNo || '';
  }
  if (matches(varKey, 'place_of_incorporation', 'placeofincorporation')) {
    return incorporationData.placeOfIncorporation || 'Malaysia';
  }
  // 사업 코드 / 설명 / MSIC (Section 14: business_nature_*, msic_code_*)
  if (matches(varKey, 'business_nature_1', 'businessnature1', 'business_1', 'business1')) {
    return incorporationData.business_1 || '';
  }
  if (matches(varKey, 'business_nature_2', 'businessnature2', 'business_2', 'business2')) {
    return incorporationData.business_2 || '';
  }
  if (matches(varKey, 'business_nature_3', 'businessnature3', 'business_3', 'business3')) {
    return incorporationData.business_3 || '';
  }
  if (matches(varKey, 'msic_code_1', 'msiccode1', 'msic_1', 'msic1')) {
    return incorporationData.msic_1 || '';
  }
  if (matches(varKey, 'msic_code_2', 'msiccode2', 'msic_2', 'msic2')) {
    return incorporationData.msic_2 || '';
  }
  if (matches(varKey, 'msic_code_3', 'msiccode3', 'msic_3', 'msic3')) {
    return incorporationData.msic_3 || '';
  }
  if (matches(varKey, 'business_description', 'businessdescription')) {
    return incorporationData.business_description || '';
  }
  // 총 발행 주식 수 (shareholders.totalNumberOfShares 합계 또는 첫 shareholder 값)
  if (matches(varKey, 'total_no_of_shares', 'totalnoofshares', 'total_shares', 'totalshares')) {
    const sh = (incorporationData.shareholders || [])[0];
    const total = sh?.totalNumberOfShares || sh?.total_shares;
    if (total) return String(total);
    const sum = (incorporationData.shareholders || [])
      .reduce((acc: number, s: any) => acc + Number(s?.numberOfShares || s?.number_of_shares || 0), 0);
    return sum > 0 ? String(sum) : '';
  }
  // 자본 / 최소자본
  if (matches(varKey, 'minimum_capital', 'minimumcapital')) {
    return String(incorporationData.minimumCapital || '');
  }
  if (matches(varKey, 'required_local_director', 'requiredlocaldirector')) {
    return incorporationData.requiredLocalDirector || '';
  }
  if (matches(varKey, 'register_shareholder', 'registershareholder')) {
    return incorporationData.registerShareholder || '';
  }
  if (matches(varKey, 'how_heard_about_us', 'howheardaboutus')) {
    return incorporationData.howHeardAboutUs || '';
  }
  // 주소 객체(address) 내부 평탄 필드
  if (matches(varKey, 'registered_address', 'registeredaddress')) {
    return resolveCompanyRegisteredAddress(companyAddr);
  }
  if (matches(varKey, 'registered_email', 'registeredemail')) {
    return companyAddr.registered_email || companyAddr.registeredAddressEmail || '';
  }
  if (matches(varKey, 'office_no', 'officeno', 'office_number')) {
    return companyAddr.office_no || companyAddr.registeredOfficeNumber || '';
  }
  if (matches(varKey, 'fax_no', 'faxno', 'fax_number')) {
    return companyAddr.fax_no || companyAddr.registeredFaxNumber || '';
  }
  if (matches(varKey, 'business_address', 'businessaddress')) {
    return resolveCompanyBusinessAddress(companyAddr);
  }
  if (matches(varKey, 'business_email', 'businessemail')) {
    return companyAddr.business_email || companyAddr.businessAddressEmail || '';
  }
  if (matches(varKey, 'business_office_no', 'businessofficeno', 'business_office_number')) {
    return companyAddr.business_office_no || companyAddr.businessOfficeNumber || '';
  }
  if (matches(varKey, 'business_fax_no', 'businessfaxno', 'business_fax_number')) {
    return companyAddr.business_fax_no || companyAddr.businessFaxNumber || '';
  }
  // ETC
  const etc = (incorporationData.etc || {}) as Record<string, any>;
  if (matches(varKey, 'financial_year_end', 'financialyearend', 'fye')) {
    return etc.financialYearEnd || etc.financialYearEndDate || '';
  }
  if (matches(varKey, 'monies_transacted', 'moniestransacted')) return etc.moniesTransacted || '';
  if (matches(varKey, 'monies_transacted_amount', 'moniestransactedamount')) {
    return String(etc.moniesTransactedAmount || '');
  }
  if (matches(varKey, 'monies_transacted_frequency', 'moniestransactedfrequency')) {
    return etc.moniesTransactedFrequency || '';
  }
  if (matches(varKey, 'sources_of_funds', 'sourcesoffunds')) return etc.sourcesOfFunds || '';

  // Filer (신청자)
  const filer = (incorporationData.formFiler || {}) as Record<string, any>;
  if (matches(varKey, 'filer_name', 'filername', 'lodger_name', 'lodgername')) {
    return filer.name || `${filer.firstName || ''} ${filer.lastName || ''}`.trim();
  }
  if (matches(varKey, 'filer_email', 'fileremail', 'lodger_email')) return filer.email || '';
  if (matches(varKey, 'filer_mobile', 'filermobile', 'lodger_mobile')) return filer.mobile || filer.mobileNumber || '';

  // ── 마지막 폴백: incorporationData top-level에 동일 키가 있으면 그대로 사용 ──
  //   (mapper가 명시하지 않은 신규 필드도 자동 매칭)
  const direct = (incorporationData as Record<string, any>)[varKey];
  if (direct !== undefined && direct !== null && typeof direct !== 'object' && String(direct).trim() !== '') {
    return String(direct);
  }

  return null;
}

// ── Director Fields (CDD Individual + Section201) ─────────────

export function mapDirectorFields(
  varKey: string,
  director: IncorporationQAState['directors'][0],
  rawDirector?: Record<string, any>,
): string | null {
  const details = rawDirector?.myDirectorDetails || rawDirector?.myDetails || {};
  const addr = details.address || {};

  // ── 이름 ──
  //   'name' 단독 varKey (Section 201 등) 도 포함; signer_/company_/entity_ 계열은 제외
  const isPlainName =
    /^name$/i.test(varKey) &&
    !/^(signer|signatur|signator|company|entity|corporate|business|member|shareholder|filer|lodger)/i.test(varKey);
  if (
    isPlainName ||
    matches(varKey, 'ind_full_name', 'fullname', 'directorname', 'contactname', 'pt_employer_name')
  ) {
    return director.name || '';
  }
  if (matches(varKey, 'contactfamilyname', 'contact_family_name', 'familyname', 'lastname', 'surname')) {
    return rawDirector?.lastName || director.name.split(' ').pop() || '';
  }
  if (matches(varKey, 'contactgivenname', 'contact_given_name', 'givenname', 'firstname')) {
    return rawDirector?.firstName || director.name.split(' ').slice(0, -1).join(' ') || '';
  }

  // ── IC/Passport 번호 ──
  if (matches(varKey, 'ind_ic_passport_no', 'ic_passport', 'contactidno', 'contact_id_no', 'idno', 'identificationno', 'nric', 'passport', 'icno')) {
    const idType = details.idType || director.idType || rawDirector?.director_id_type || rawDirector?.idType || '';
    const idNo =
      details.idNumber ||
      director.idNo ||
      rawDirector?.director_id_no ||
      rawDirector?.identificationNo ||
      rawDirector?.idNo ||
      rawDirector?.nricNo ||
      rawDirector?.passportNo ||
      rawDirector?.contactRegNo ||
      rawDirector?.nric ||
      rawDirector?.passport ||
      details.nricNo ||
      details.passportNo ||
      details.contactRegNo ||
      '';
    return formatIdNumberForDoc(idNo, idType);
  }

  // ── 생년월일 ──
  if (matches(varKey, 'ind_dob', 'contactdob', 'contact_dob', 'dob', 'dateofbirth', 'date_of_birth')) {
    return details.dateOfBirth || '';
  }

  // ── 국적 ──
  if (matches(varKey, 'ind_nationality', 'pt_nationality', 'nationality', 'citizen', 'contactnationality', 'contact_nationality')) {
    return details.nationality || director.nationality || '';
  }

  // ── 성별 ──
  if (matches(varKey, 'ind_gender', 'gender')) {
    return details.gender || '';
  }

  // ── 인종 ──
  if (matches(varKey, 'ind_race', 'race')) {
    return details.race || '';
  }

  // ── 주소 (residential) ──
  if (matches(varKey, 'ind_residential_address', 'contactresidentialaddress', 'contact_residential_address', 'residentialaddress', 'director_address')) {
    return resolveDirectorFullAddress(rawDirector) || formatStreetOnly(addr);
  }
  if (matches(varKey, 'ind_town', 'town', 'director_town') && !matches(varKey, 'mailing')) {
    return resolveDirectorFlatField(rawDirector, 'city');
  }
  if (matches(varKey, 'ind_state', 'state', 'director_state') && !matches(varKey, 'mailing')) {
    return resolveDirectorFlatField(rawDirector, 'state');
  }
  if (matches(varKey, 'ind_postcode', 'postcode', 'director_postcode') && !matches(varKey, 'mailing')) {
    return resolveDirectorFlatField(rawDirector, 'postcode');
  }
  if (matches(varKey, 'ind_country', 'country') && !matches(varKey, 'mailing') && !matches(varKey, 'nationality')) {
    return formatAddrDisplayPart(resolveAddrField(addr, 'country'));
  }

  // ── Mailing address (same as residential) ──
  if (matches(varKey, 'ind_mailing_address', 'mailingaddress')) {
    return formatStreetOnly(addr) || resolveDirectorFullAddress(rawDirector);
  }
  if (matches(varKey, 'ind_mailing_town', 'mailingtown')) {
    return resolveDirectorFlatField(rawDirector, 'city');
  }
  if (matches(varKey, 'ind_mailing_state', 'mailingstate')) {
    return resolveDirectorFlatField(rawDirector, 'state');
  }
  if (matches(varKey, 'ind_mailing_postcode', 'mailingpostcode')) {
    return resolveDirectorFlatField(rawDirector, 'postcode');
  }
  if (matches(varKey, 'ind_mailing_country', 'mailingcountry')) {
    return formatAddrDisplayPart(resolveAddrField(addr, 'country'));
  }

  // ── 직업 / Occupation ──
  if (matches(varKey, 'ind_occupation_type', 'pt_occupation', 'occupation', 'occupationtype')) {
    return 'Director';
  }

  // ── 연락처 ──
  if (matches(varKey, 'pt_contact_no', 'ind_contact_no', 'contacttelephone', 'contact_telephone', 'telephone', 'phone', 'mobile', 'tel')) {
    return details.mobileNumber || rawDirector?.contactNumber || '';
  }
  if (matches(varKey, 'contactemail', 'contact_email', 'email', 'ind_email', 'pt_email')) {
    return rawDirector?.email || director.email || '';
  }

  // ── 직위 / Position / Designation ──
  if (matches(varKey, 'contactposition', 'contact_position', 'position', 'designation', 'title') && !matches(varKey, 'signer')) {
    return 'Director';
  }

  // ── ID Type ──
  if (matches(varKey, 'idtype', 'id_type', 'identification_type')) {
    return details.idType || director.idType || '';
  }

  // ── Referral ──
  if (matches(varKey, 'contactreferral', 'contact_referral', 'referral')) {
    return '';
  }

  return null;
}

// ── Shareholder Fields ────────────────────────────────────────

export function mapShareholderFields(
  varKey: string,
  shareholder: IncorporationQAState['shareholders'][0],
  rawShareholder?: Record<string, any>,
): string | null {
  const details = rawShareholder?.myShareholderDetails || rawShareholder?.myDetails || {};
  const addr = details.address || {};

  // ── 법인 주주(Legal Person 서식, lp_* 변수) ──
  //   법인에는 개인용 CDD 서식이 맞지 않아 별도 서식(TMP004)을 쓴다.
  //   Lettaga 가 법인 주주에 대해 갖고 있는 값만 채우고, 모르는 항목(영업주소·주업종·
  //   BO·경영진 등)은 비워 둔다 — 추측해서 넣으면 SSM 제출 문서에 틀린 정보가 찍힌다.
  // SSM 미등록 법인(외국법인·정부기관 등)은 corporateName 이 비고 entityName 에만 이름이 있다.
  // 이걸 안 보면 corporateName 이 빈 문자열이라 아래 lp_* 분기가 통째로 건너뛰어지고,
  // Legal Person CDD 문서의 **본문 변수가 전부 빈 채로** 생성된다(파일명만의 문제가 아니다).
  const corporateName = String(
    details.corporateName || rawShareholder?.corporateName
    || details.entityName || rawShareholder?.entityName || '',
  ).trim();
  if (corporateName) {
    if (matches(varKey, 'lp_company_name')) return corporateName;
    if (matches(varKey, 'lp_registration_no')) {
      // 미등록 법인은 SSM 등록번호가 없고 Entity No. 를 쓴다.
      return String(
        details.compRegNo || rawShareholder?.registrationNo
        || details.entityNo || rawShareholder?.entityNo || '',
      ).trim();
    }
    if (matches(varKey, 'lp_country_of_incorporation')) {
      return String(details.placeOfInc || '').trim();
    }
    if (matches(varKey, 'lp_registered_address')) {
      return formatIncorporationAddressLine(shareholderAddr(rawShareholder));
    }
    if (matches(varKey, 'lp_town')) return resolveShareholderFlatField(rawShareholder, 'city');
    if (matches(varKey, 'lp_state')) return resolveShareholderFlatField(rawShareholder, 'state');
    if (matches(varKey, 'lp_postcode')) return resolveShareholderFlatField(rawShareholder, 'postcode');
    // country 는 flat 필드가 없어 주소 객체에서 직접 읽는다.
    if (matches(varKey, 'lp_country')) return resolveAddrField(shareholderAddr(rawShareholder), 'country');
    if (matches(varKey, 'lp_purpose_of_txn')) {
      return String(details.purposeOfTransaction || rawShareholder?.ind_purpose_of_txn || '').trim();
    }
    // 이 법인에 대해 Lettaga 가 아는 유일한 자연인은 대표자다.
    if (matches(varKey, 'lp_directors_partners')) {
      const rep = String(details.repName || '').trim();
      const desig = String(details.repDesignation || '').trim();
      return rep ? (desig ? `${rep} (${desig})` : rep) : '';
    }
    // 아래는 법인 주주에 대해 Lettaga 가 수집하지 않는 항목이다.
    // **빈 문자열을 명시적으로 돌려줘야** 한다 - null 을 돌려주면 상위에서 "회사(설립 대상)"
    // 컨텍스트로 폴백해, 법인 주주의 CDD 문서에 **설립하는 회사의 주소·연락처**가 찍힌다.
    if (matches(
      varKey,
      'lp_business_address', 'lp_biz_town', 'lp_biz_state', 'lp_biz_postcode', 'lp_biz_country',
      'lp_principle_business', 'lp_contact_no', 'lp_other_type', 'lp_senior_management',
    )) return '';
  }

  // ── 이름 ──
  if (matches(varKey, 'ind_full_name', 'fullname', 'shareholdername', 'contactname')) {
    return shareholder.name || '';
  }
  if (matches(varKey, 'contactfamilyname', 'contact_family_name', 'familyname', 'lastname')) {
    return rawShareholder?.lastName || shareholder.name.split(' ').pop() || '';
  }
  if (matches(varKey, 'contactgivenname', 'contact_given_name', 'givenname', 'firstname')) {
    return rawShareholder?.firstName || shareholder.name.split(' ').slice(0, -1).join(' ') || '';
  }

  // ── IC/Passport ──
  if (matches(varKey, 'ind_ic_passport_no', 'ic_passport', 'contactidno', 'contact_id_no', 'idno', 'nric', 'passport')) {
    const idType = details.idType || rawShareholder?.member_id_type || '';
    const idNo = details.idNumber || '';
    return formatIdNumberForDoc(idNo, idType);
  }

  // ── 생년월일 ──
  if (matches(varKey, 'ind_dob', 'contactdob', 'contact_dob', 'dob', 'dateofbirth')) {
    return details.dateOfBirth || '';
  }

  // ── 국적 ──
  if (matches(varKey, 'ind_nationality', 'pt_nationality', 'contactnationality', 'contact_nationality', 'nationality')) {
    return details.nationality || '';
  }

  // ── 성별 / 인종 ──
  if (matches(varKey, 'ind_gender', 'gender')) return details.gender || '';
  if (matches(varKey, 'ind_race', 'race')) return details.race || '';

  // ── 주소 ──
  if (matches(varKey, 'ind_residential_address', 'contactresidentialaddress', 'contact_residential_address', 'residentialaddress', 'member_address')) {
    return resolveShareholderFullAddress(rawShareholder) || formatStreetOnly(addr);
  }
  if (matches(varKey, 'ind_town', 'town', 'member_town') && !matches(varKey, 'mailing')) {
    return resolveShareholderFlatField(rawShareholder, 'city');
  }
  if (matches(varKey, 'ind_state', 'state', 'member_state') && !matches(varKey, 'mailing')) {
    return resolveShareholderFlatField(rawShareholder, 'state');
  }
  if (matches(varKey, 'ind_postcode', 'postcode', 'member_postcode') && !matches(varKey, 'mailing')) {
    return resolveShareholderFlatField(rawShareholder, 'postcode');
  }
  if (matches(varKey, 'ind_country', 'country') && !matches(varKey, 'mailing') && !matches(varKey, 'nationality')) {
    return formatAddrDisplayPart(resolveAddrField(addr, 'country'));
  }

  // ── Mailing address ──
  if (matches(varKey, 'ind_mailing_address', 'mailingaddress')) {
    return formatStreetOnly(addr) || resolveShareholderFullAddress(rawShareholder);
  }
  if (matches(varKey, 'ind_mailing_town')) return resolveShareholderFlatField(rawShareholder, 'city');
  if (matches(varKey, 'ind_mailing_state')) return resolveShareholderFlatField(rawShareholder, 'state');
  if (matches(varKey, 'ind_mailing_postcode')) return resolveShareholderFlatField(rawShareholder, 'postcode');
  if (matches(varKey, 'ind_mailing_country')) return formatAddrDisplayPart(resolveAddrField(addr, 'country'));

  // ── 직업 ──
  if (matches(varKey, 'ind_occupation_type', 'pt_occupation', 'occupation')) return 'Shareholder';

  // ── 연락처 ──
  if (matches(varKey, 'pt_contact_no', 'ind_contact_no', 'contacttelephone', 'contact_telephone', 'phone', 'mobile')) {
    return details.mobileNumber || rawShareholder?.contactNumber || '';
  }
  if (matches(varKey, 'contactemail', 'contact_email', 'email', 'ind_email', 'pt_email')) {
    return rawShareholder?.email || shareholder.email || '';
  }

  // ── Position ──
  if (matches(varKey, 'contactposition', 'contact_position', 'position', 'designation') && !matches(varKey, 'signer')) {
    return 'Shareholder';
  }

  // ── Employer (고용주) ──
  if (matches(varKey, 'pt_employer_name', 'employer')) {
    return shareholder.name || '';
  }

  // ── 주식 관련 ──
  if (matches(varKey, 'sharetype', 'shareholdertype', 'classofshare', 'class_of_share')) {
    return details.classOfShare || shareholder.shareType || '';
  }
  if (matches(varKey, 'numberofshares', 'number_of_shares', 'shares')) {
    return String(rawShareholder?.numberOfShares || shareholder.shares || '');
  }
  if (matches(varKey, 'pricepershare', 'price_per_share')) {
    return String(details.pricePerShare || '');
  }

  return null;
}

// ── Auto Map Variables ────────────────────────────────────────

export function autoMapVariables(
  schema: Array<{ varKey: string; varLabel: string }>,
  incorporationData: Record<string, any>,
  qa: IncorporationQAState,
  context: 'company' | 'director' | 'shareholder' | 'section14',
  personIndex: number = 0,
): Record<string, string> {
  const formData: Record<string, string> = {};

  for (const field of schema) {
    let value: string | null = null;

    // 1. 컨텍스트별 인물 정보 매핑 (우선)
    if (context === 'director') {
      const director = qa.directors[personIndex];
      const rawDir = (incorporationData.directors || [])[personIndex];
      if (director) {
        value = mapDirectorFields(field.varKey, director, rawDir);
      }
    }

    if (context === 'shareholder') {
      const shareholder = qa.shareholders[personIndex];
      const rawSh = (incorporationData.shareholders || [])[personIndex];
      if (shareholder) {
        value = mapShareholderFields(field.varKey, shareholder, rawSh);
      }
    }

    // 2. 회사 정보 매핑 (인물 매핑 안 된 경우)
    if (value === null) {
      value = mapCompanyFields(field.varKey, incorporationData, qa);
    }

    if (value !== null && value !== '') {
      formData[field.varKey] = value;
    }
  }

  // Signer 자동 매핑 — 네이밍 규약으로 UI 피커와 스칼라 텍스트를 구분
  //  · UI 피커(JSON 배열로 주입): varKey가 signer/signers/signature(s)/signatory(ies)로 **끝**남
  //      예) [[Signers]], [[direct_signer]], [[authorized_signatory]]
  //  · 스칼라 텍스트(primary signer의 필드를 문자열로 주입): 같은 어근 + `_`로 **시작**
  //      예) [[signer_name]], [[signer_designation]], [[signatory_title]]
  const SIGNER_ROOT = '(signers?|signatur(?:e|es)|signator(?:y|ies))';
  const UI_PICKER_RE = new RegExp(`(^|_)${SIGNER_ROOT}$`, 'i');
  const SCALAR_RE = new RegExp(`^${SIGNER_ROOT}_`, 'i');

  const uiPickerFields = schema.filter(f => UI_PICKER_RE.test(f.varKey));
  const scalarSignerFields = schema.filter(f => SCALAR_RE.test(f.varKey));

  const signers = (uiPickerFields.length > 0 || scalarSignerFields.length > 0)
    ? buildSigners(qa, context, personIndex)
    : [];
  const primary = signers[0];

  if (uiPickerFields.length > 0 && signers.length > 0) {
    const signerJson = JSON.stringify(signers);
    for (const sf of uiPickerFields) {
      formData[sf.varKey] = signerJson;
    }
  }

  if (scalarSignerFields.length > 0 && primary) {
    for (const sf of scalarSignerFields) {
      const suffix = sf.varKey.replace(SCALAR_RE, '').toLowerCase();
      let val: string = '';
      if (/(^|_)name(s)?$/.test(suffix) || /full[_\-]?name/.test(suffix)) val = primary.name || '';
      else if (/(designation|title|role|position)/.test(suffix)) val = primary.title || '';
      else if (/email/.test(suffix)) val = primary.email || '';
      else val = primary.name || '';
      formData[sf.varKey] = val;
    }
  }

  // CDD 등 — 주소 한 줄은 채워졌는데 ind_town/state/postcode 가 비는 경우 보강
  if (context === 'director' || context === 'shareholder') {
    const rawPerson = context === 'director'
      ? (incorporationData.directors || [])[personIndex]
      : (incorporationData.shareholders || [])[personIndex];
    const resolveFlat = context === 'director' ? resolveDirectorFlatField : resolveShareholderFlatField;
    const schemaKeys = new Set(schema.map((f) => f.varKey));
    const injectFlat = (varKey: string, kind: 'postcode' | 'city' | 'state') => {
      if (!schemaKeys.has(varKey)) return;
      const existing = String(formData[varKey] || '').trim();
      if (existing && !isCodeValue(existing)) return;
      const v = resolveFlat(rawPerson, kind);
      if (v) formData[varKey] = v;
    };
    injectFlat('ind_postcode', 'postcode');
    injectFlat('ind_town', 'city');
    injectFlat('ind_state', 'state');
    injectFlat('ind_mailing_postcode', 'postcode');
    injectFlat('ind_mailing_town', 'city');
    injectFlat('ind_mailing_state', 'state');
    if (context === 'director') {
      injectFlat('director_postcode', 'postcode');
      injectFlat('director_town', 'city');
      injectFlat('director_state', 'state');
    } else {
      injectFlat('member_postcode', 'postcode');
      injectFlat('member_town', 'city');
      injectFlat('member_state', 'state');
    }
  }

  // 반복부 데이터 주입 (Section 14 의 [[if director]] / [[if member]] 블록용)
  //   각 항목의 key는 템플릿 placeholder varKey 와 일치해야 함 (director_name, member_name 등)
  //   배열은 백엔드 expandRepeatingSections 에서 반복부 확장에 사용됨
  if (context === 'section14') {
    const dirsRaw = (incorporationData.directors || []) as any[];
    (formData as any).directors = dirsRaw.map((d: any) => ({
      director_name: d.director_name || d.name || `${d.firstName || ''} ${d.lastName || ''}`.trim(),
      director_id_type: d.director_id_type || d.myDirectorDetails?.idType || d.myDetails?.idType || d.idType || '',
      director_id_no: formatIdNumberForDoc(
        d.director_id_no || d.myDirectorDetails?.idNumber || d.myDetails?.idNumber || d.identificationNo || d.idNo || '',
        d.director_id_type || d.myDirectorDetails?.idType || d.myDetails?.idType || d.idType || '',
      ),
      director_nationality: d.director_nationality || d.myDirectorDetails?.nationality || d.myDetails?.nationality || d.nationality || '',
      director_address: resolveDirectorFullAddress(d),
      director_email: d.director_email || d.myDirectorDetails?.email || d.myDetails?.email || d.email || '',
      director_dob: d.director_dob || d.myDirectorDetails?.dateOfBirth || d.myDetails?.dateOfBirth || d.dateOfBirth || '',
      director_race: d.director_race || d.myDirectorDetails?.race || d.myDetails?.race || d.race || '',
      director_postcode: resolveDirectorFlatField(d, 'postcode'),
      director_town: resolveDirectorFlatField(d, 'city'),
      director_state: resolveDirectorFlatField(d, 'state'),
    }));

    const memsRaw = (incorporationData.shareholders || []) as any[];
    (formData as any).members = memsRaw.map((s: any) => ({
      member_name: s.member_name || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim(),
      member_id_type: s.member_id_type || s.myShareholderDetails?.idType || s.myDetails?.idType || s.idType || '',
      member_id_no: formatIdNumberForDoc(
        s.member_id_no || s.myShareholderDetails?.idNumber || s.myDetails?.idNumber || '',
        s.member_id_type || s.myShareholderDetails?.idType || s.myDetails?.idType || s.idType || '',
      ),
      member_nationality: s.member_nationality || s.myShareholderDetails?.nationality || s.myDetails?.nationality || s.nationality || '',
      member_address: resolveShareholderFullAddress(s),
      member_email: s.member_email || s.myShareholderDetails?.email || s.myDetails?.email || s.email || '',
      member_race: s.member_race || s.myShareholderDetails?.race || s.myDetails?.race || '',
      member_postcode: resolveShareholderFlatField(s, 'postcode'),
      member_town: resolveShareholderFlatField(s, 'city'),
      member_state: resolveShareholderFlatField(s, 'state'),
      member_price_per_share: String(s.price_per_share || s.myShareholderDetails?.pricePerShare || s.pricePerShare || ''),
      member_no_of_shares: String(s.number_of_shares || s.numberOfShares || ''),
      member_class_of_share: s.class_of_share || s.myShareholderDetails?.classOfShare || s.classOfShare || '',
    }));
  }

  return formData;
}

// ── Build Signers ─────────────────────────────────────────────

function buildSigners(
  qa: IncorporationQAState,
  context: 'company' | 'director' | 'shareholder' | 'section14',
  personIndex: number,
): Array<{ id: string; name: string; title: string; role: string; email: string; showTitle: boolean }> {
  if (context === 'director') {
    const dir = qa.directors[personIndex];
    if (!dir) return [];
    return [{
      id: dir.contactCd || `dir-${personIndex}`,
      name: dir.name,
      title: 'Director',
      role: 'director',
      email: dir.email || '',
      showTitle: true,
    }];
  }

  if (context === 'section14') {
    return qa.directors.map((dir, idx) => ({
      id: dir.contactCd || `dir-${idx}`,
      name: dir.name,
      title: 'Director',
      role: 'director',
      email: dir.email || '',
      showTitle: true,
    }));
  }

  if (context === 'shareholder') {
    const sh = qa.shareholders[personIndex];
    if (!sh) return [];
    return [{
      id: sh.contactCd || `sh-${personIndex}`,
      name: sh.name,
      title: 'Shareholder',
      role: 'shareholder',
      email: sh.email || '',
      showTitle: true,
    }];
  }

  if (context === 'company') {
    const dir = qa.directors[0];
    if (!dir) return [];
    return [{
      id: dir.contactCd || 'dir-0',
      name: dir.name,
      title: 'Director',
      role: 'director',
      email: dir.email || '',
      showTitle: true,
    }];
  }

  return [];
}
